import { useCurrentUser } from "@/contexts/user";
import { useConfigStore } from "@/store/config";
import { useTokenStore } from "@/store/token";
import {
  BulbOutlined,
  DownOutlined,
  FilePdfOutlined,
  HomeOutlined,
  LogoutOutlined,
  MoonOutlined,
  SafetyOutlined,
  TeamOutlined,
  TranslationOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import {
  theme as antdTheme,
  Layout as AntLayout,
  Button,
  Dropdown,
  Menu,
  Segmented,
  Space,
  Switch,
  Typography,
} from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";

const { Header, Content, Sider } = AntLayout;

export default function Layout() {
  const { t, i18n } = useTranslation();
  const { logout } = useTokenStore();
  const { theme, setTheme, setLanguage } = useConfigStore();
  const navigate = useNavigate();
  const isDark = theme === "dark";

  const { isAdmin } = useCurrentUser();

  if (!isAdmin) {
    return <Navigate to="/create" replace />;
  }

  const currentLanguage = useMemo(() => {
    const lang = i18n.language || "zh-CN";
    return lang.startsWith("en") ? "en" : "zh-CN";
  }, [i18n.language]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.theme = theme;
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  const handleLanguageChange = (value: string | number) => {
    const nextLang = value === "en" ? "en" : "zh-CN";
    i18n.changeLanguage(nextLang).then(() => {});
    setLanguage(nextLang);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const accountMenuItems: MenuProps["items"] = [
    {
      key: "create",
      icon: <HomeOutlined />,
      label: t("nav.create"),
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("nav.logout"),
    },
  ];

  const onAccountMenuClick: MenuProps["onClick"] = async ({ key }) => {
    if (key === "create") {
      navigate("/create");
      return;
    }
    if (key === "logout") {
      await handleLogout();
    }
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "/admin/users",
      icon: <TeamOutlined />,
      label: t("nav.users") + t("nav.admin"),
    },
    {
      key: "/admin/pixel-art",
      icon: <BulbOutlined />,
      label: t("pixelArt.title"),
    },
    {
      key: "/admin/audit",
      icon: <SafetyOutlined />,
      label: t("nav.auditLogs") + t("nav.admin"),
    },
  ];

  const onClick: MenuProps["onClick"] = (e) => {
    navigate(e.key);
  };

  const selectedKey = useLocation().pathname;
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = antdTheme.useToken();

  return (
    <AntLayout className="brand-layout-shell">
      <Header className="brand-header sticky top-0 z-10 h-auto px-4 py-3 sm:px-6">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="leading-tight">
              <h1 className={isDark ? "text-sky-300" : "text-sky-700"}>
                {t("app.title")}
              </h1>
              <Typography.Text className="block text-xs tracking-[0.35em]">
                {t("app.suite")}
              </Typography.Text>
            </div>
            <Space size="middle" wrap>
              <Space size="small">
                <TranslationOutlined />
                <Segmented
                  size="small"
                  value={currentLanguage}
                  options={[
                    { label: t("language.chinese"), value: "zh-CN" },
                    { label: t("language.english"), value: "en" },
                  ]}
                  onChange={handleLanguageChange}
                />
              </Space>
              <Space size="small">
                <Switch
                  checked={isDark}
                  onChange={(checked) => setTheme(checked ? "dark" : "light")}
                  checkedChildren={<MoonOutlined />}
                  unCheckedChildren={<BulbOutlined />}
                />
                <Typography.Text type="secondary">
                  {isDark ? t("common.dark") : t("common.light")}
                </Typography.Text>
              </Space>
              <Space size="small">
                <Button
                  type={isAdmin ? "primary" : "text"}
                  icon={<FilePdfOutlined />}
                  onClick={() => navigate("/create")}
                >
                  {t("nav.create")}
                </Button>
                <Dropdown
                  menu={{
                    items: accountMenuItems,
                    onClick: onAccountMenuClick,
                  }}
                  trigger={["click"]}
                >
                  <Button icon={<DownOutlined />}>{t("common.actions")}</Button>
                </Dropdown>
              </Space>
            </Space>
          </div>
        </div>
      </Header>
      <AntLayout>
        <Sider width={200} style={{ background: colorBgContainer }}>
          <Menu
            mode="inline"
            className="overflow-x-auto rounded-2xl border-b-0 bg-transparent px-2 py-1 [&.ant-menu]:bg-transparent [&.ant-menu-horizontal>.ant-menu-item::after]:border-b-0! [&.ant-menu-horizontal>.ant-menu-submenu::after]:border-b-0! [&_.ant-menu-item]:rounded-full [&_.ant-menu-item-selected]:bg-sky-500/15! [&_.ant-menu-item-selected]:text-current!"
            selectedKeys={[selectedKey]}
            items={menuItems}
            onClick={onClick}
          />
        </Sider>
        <Content
          className="brand-content-shell px-4 pb-10 pt-6 sm:px-6 lg:px-8"
          style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}
        >
          <div className="brand-panel px-4 py-5 sm:px-6 sm:py-6">
            <Outlet />
          </div>
        </Content>
      </AntLayout>
    </AntLayout>
  );
}
